import { useState } from "react";
import { showSuccess, showError } from "@/lib/toastHelpers";
import {
  useWorkspaceInvitations,
  useCreateWorkspaceInvitation,
  useCreateLinkInvitation,
  useRevokeInvitation,
  useRoles,
  type WorkspaceInvitation,
} from "@/hooks/useTeam";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Link2, Copy, Trash2, Plus, Mail, Users } from "lucide-react";
import { format } from "date-fns";
import { BatchInviteDialog } from "./BatchInviteDialog";

// ─── Types ────────────────────────────────────────────────────────────────────

interface InvitationsTabProps {
  /** workspaceId for all workspace-scoped API calls */
  workspaceId: string;
  /** accountId for role lookup (team.js /roles endpoint) */
  accountId: string;
}

// ─── Status badge helper ──────────────────────────────────────────────────────

function StatusBadge({ status }: { status: WorkspaceInvitation["status"] }) {
  const variants: Record<string, string> = {
    pending:  "bg-yellow-100 text-yellow-800",
    accepted: "bg-green-100 text-green-800",
    expired:  "bg-gray-100 text-gray-600",
    revoked:  "bg-red-100 text-red-800",
    declined: "bg-orange-100 text-orange-800",
  };
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${variants[status] ?? "bg-gray-100"}`}>
      {status}
    </span>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function InvitationsTab({ workspaceId, accountId }: InvitationsTabProps) {
  // Data
  const { data: invitations, isLoading: loadingInvitations } =
    useWorkspaceInvitations(workspaceId);
  const { data: roles, isLoading: loadingRoles } = useRoles(accountId);

  // Mutations — scoped to this workspaceId
  const createEmailInvite  = useCreateWorkspaceInvitation(workspaceId);
  const createLinkInvite   = useCreateLinkInvitation(workspaceId);
  const revokeInvitation   = useRevokeInvitation(workspaceId);

  // Dialog state
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [linkDialogOpen,  setLinkDialogOpen]  = useState(false);
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);

  // Email invite form
  const [emailFormData, setEmailFormData] = useState({ email: "", role: "member" });

  // Link invite form
  const [linkFormData, setLinkFormData] = useState({ role: "member", maxUses: "", expiryDays: "7" });

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleSendEmailInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createEmailInvite.mutateAsync(emailFormData);
      showSuccess(`Invitation sent to ${emailFormData.email}.`, "Invitation sent");
      setEmailDialogOpen(false);
      setEmailFormData({ email: "", role: "member" });
    } catch (error: any) {
      showError(error.message ?? "Failed to send invitation.", "Error");
    }
  };

  const handleCreateLink = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createLinkInvite.mutateAsync({
        role:       linkFormData.role,
        maxUses:    linkFormData.maxUses ? parseInt(linkFormData.maxUses) : null,
        expiryDays: linkFormData.expiryDays ? parseInt(linkFormData.expiryDays) : 7,
      });
      showSuccess("Invitation link created.", "Link created");
      setLinkDialogOpen(false);
      setLinkFormData({ role: "member", maxUses: "", expiryDays: "7" });
    } catch (error: any) {
      showError(error.message ?? "Failed to create link.", "Error");
    }
  };

  const handleRevoke = async (id: string) => {
    try {
      await revokeInvitation.mutateAsync({ id });
      showSuccess("Invitation revoked.", "Revoked");
    } catch (error: any) {
      showError(error.message ?? "Failed to revoke invitation.", "Error");
    }
  };

  const copyLink = (token: string) => {
    const url = `${window.location.origin}/join/${token}`;
    navigator.clipboard.writeText(url);
    showSuccess("Invitation link copied to clipboard.", "Copied");
  };

  // ── Loading state ──────────────────────────────────────────────────────────

  if (loadingInvitations || loadingRoles) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      {/* ── Toolbar ── */}
      <div className="flex flex-wrap gap-2">
        {/* Single email invite */}
        <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Mail className="w-4 h-4 mr-2" />
              Invite by Email
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Invite Team Member</DialogTitle>
              <DialogDescription>
                Send an invitation email to a specific address.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSendEmailInvite} className="space-y-4">
              <div>
                <Label>Email Address *</Label>
                <Input
                  type="email"
                  value={emailFormData.email}
                  onChange={(e) => setEmailFormData({ ...emailFormData, email: e.target.value })}
                  placeholder="colleague@example.com"
                  required
                />
              </div>
              <div>
                <Label>Role *</Label>
                <Select
                  value={emailFormData.role}
                  onValueChange={(v) => setEmailFormData({ ...emailFormData, role: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {roles?.map((r) => (
                      <SelectItem key={r._id} value={r.name}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" disabled={createEmailInvite.isPending} className="w-full">
                {createEmailInvite.isPending ? "Sending…" : "Send Invitation"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Reusable link invite */}
        <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
          <DialogTrigger asChild>
            <Button variant="outline">
              <Link2 className="w-4 h-4 mr-2" />
              Create Link
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Invitation Link</DialogTitle>
              <DialogDescription>
                Generate a reusable link that anyone can use to join.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateLink} className="space-y-4">
              <div>
                <Label>Role *</Label>
                <Select
                  value={linkFormData.role}
                  onValueChange={(v) => setLinkFormData({ ...linkFormData, role: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {roles?.map((r) => (
                      <SelectItem key={r._id} value={r.name}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Max Uses (optional)</Label>
                <Input
                  type="number"
                  min="1"
                  value={linkFormData.maxUses}
                  onChange={(e) => setLinkFormData({ ...linkFormData, maxUses: e.target.value })}
                  placeholder="Unlimited"
                />
              </div>
              <div>
                <Label>Expires In (days)</Label>
                <Input
                  type="number"
                  min="1"
                  value={linkFormData.expiryDays}
                  onChange={(e) => setLinkFormData({ ...linkFormData, expiryDays: e.target.value })}
                  placeholder="7"
                />
              </div>
              <Button type="submit" disabled={createLinkInvite.isPending} className="w-full">
                {createLinkInvite.isPending ? "Creating…" : "Create Link"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Batch invite */}
        <Button variant="outline" onClick={() => setBatchDialogOpen(true)}>
          <Users className="w-4 h-4 mr-2" />
          Batch Invite
        </Button>
      </div>

      {/* ── Batch dialog (controlled externally — no internal trigger) ── */}
      <BatchInviteDialog
        open={batchDialogOpen}
        onOpenChange={setBatchDialogOpen}
        workspaceId={workspaceId}
        roles={roles?.map((r) => ({ _id: r._id, name: r.name })) ?? []}
      />

      {/* ── Invitation list ── */}
      {!invitations?.length ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <Link2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No invitations yet</p>
              <p className="text-xs text-muted-foreground mt-1">
                Use the buttons above to invite team members.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {invitations.map((invite) => (
            <InvitationCard
              key={invite._id}
              invite={invite}
              onRevoke={handleRevoke}
              onCopyLink={copyLink}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── InvitationCard (pure display) ───────────────────────────────────────────

interface InvitationCardProps {
  invite: WorkspaceInvitation;
  onRevoke: (id: string) => void;
  onCopyLink: (token: string) => void;
}

function InvitationCard({ invite, onRevoke, onCopyLink }: InvitationCardProps) {
  const isRevokable = invite.status === "pending";

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <CardTitle className="text-base">
                {invite.email ?? "Link invitation"}
              </CardTitle>
              <StatusBadge status={invite.status} />
            </div>
            <CardDescription>
              {invite.role} ·{" "}
              {invite.type === "link" ? "Reusable link" : "Email invite"} ·{" "}
              Created {format(new Date(invite.createdAt), "MMM d, yyyy")}
            </CardDescription>
          </div>

          {isRevokable && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onRevoke(invite._id)}
              title="Revoke invitation"
            >
              <Trash2 className="w-4 h-4 text-destructive" />
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-2">
        {/* Link + copy button (only for link-type invites) */}
        {invite.type === "link" && (
          <div className="flex items-center gap-2">
            <Input
              value={`${window.location.origin}/join/${invite.token}`}
              readOnly
              className="font-mono text-xs"
            />
            <Button variant="outline" size="icon" onClick={() => onCopyLink(invite.token)}>
              <Copy className="w-4 h-4" />
            </Button>
          </div>
        )}

        {/* Usage and expiry metadata */}
        <div className="flex gap-4 text-xs text-muted-foreground">
          {invite.type === "link" && (
            <span>Uses: {invite.usedCount} / {invite.maxUses ?? "∞"}</span>
          )}
          {invite.expiresAt && (
            <span>Expires: {format(new Date(invite.expiresAt), "MMM d, yyyy")}</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
